/**
 * Locale translations: ja (Japanese).
 * Keys and structure are shared across all locale files.
 */
export const ja = {
  // Common
  common: {
    loading: "読み込み中...",
    error: "エラー",
    sort: "並べ替え",
    sortAsc: "昇順",
    sortDesc: "降順",
    save: "保存",
    cancel: "キャンセル",
    delete: "削除",
    remove: "取り除く",
    edit: "編集",
    view: '表示',
    copy: "コピー",
    create: "作成",
    update: "更新",
    search: "検索",
    actions: "操作",
    code: "コード",
    all: "すべて",
    clear: "クリア",
    filter: "フィルター",
    yes: "はい",
    no: "いいえ",
    confirm: "確認",
    close: "閉じる",
    select: "選択",
    active: "有効",
    inactive: "無効",
    success: "成功",
    updated: "更新しました",
    created: "作成しました",
    deleted: "削除しました",
    confirmDelete: "削除してもよろしいですか？",
    errorOccurred: "エラーが発生しました",
    noData: "データがありません",
    status: "ステータス",
    show: "表示",
    hide: "非表示",
    searchPlaceholder: "検索...",
    allStatuses: "すべてのステータス",
    clearFilters: "フィルターをクリア",
    newRecord: "新規レコード",
    noPermission: "権限がありません",
    statusDistribution: "ステータス分布",
    rows: "行",
    firstPage: "最初のページ",
    previousPage: "前のページ",
    nextPage: "次のページ",
    lastPage: "最後のページ",
    page: "ページ",
    optional: "任意",
    saving: "保存中...",
    deleteConfirmTitle: "削除の確認",
    deleteWarning: "この操作は取り消せません！",
    autoTranslate: "自動翻訳",
    autoTranslateSuccessCount: "{count} 個のアイテムを自動翻訳しました。",
    autoTranslateNoSource: "ソース翻訳が見つかりません。",
    enterTargetLanguage: "ターゲット言語を選択してください。",
    sourceLanguageLabel: "ソース言語（インターフェース）",
    targetLanguage: "ターゲット言語",
    sourceAndTargetMustDiffer: "ソースと言語は異なる必要があります。",
    context: "文脈",
    key: "キー",
    value: "値",
    locale: "言語",
    auto: "自動",
    manual: "手動",
    manageLocales: "言語管理",
    activeLocalesDescription: "アクティブな言語を選択してください。設定としてのみ保存されます。",
    updateUntranslatedFields: "新しいフィールドを同期",
    syncEntriesResult: "{count} 件の新しい翻訳エントリを同期しました。",
    syncDeletedResult: "{count} 件の古いエントリを削除しました。",
    selectSourceLanguageForSync: "コンテンツは何語ですか？",
    sourceLanguage: "ソース言語",
    originalShort: "原文",
    languageNames: { en: "英語", tr: "トルコ語", de: "ドイツ語", fr: "フランス語", es: "スペイン語", ar: "アラビア語", it: "イタリア語", ja: "日本語", zh: "中国語", ru: "ロシア語" } as Record<string, string>,
    perPage: "表示件数",
    darkMode: "ダークモード",
    lightMode: "ライトモード",
    copyright: "© {year} QubePro. All rights reserved.",
    translationRates: "翻訳率",
    profileMenu: "プロフィールメニュー",
    manageYourAccount: "アカウントを管理",
    adminConsole: "管理者コンソール",
    serverConfiguring: "サーバー構成中、少々お待ちください...",
    productName: "管理",
  },

  portal: {
    searchPlaceholder: "アプリを検索...",
    noAppsFound: "アプリが見つかりません。",
    quickLinks: "クイックリンク",
    console: "管理者コンソール",
    lastLogin: "最終ログイン",
    apps: {
      core: { name: "管理", description: "企業、組織、ユーザー、役割、権限を一元管理されたプラットフォームで管理します。" },
      bi: { name: "アナリティクス", description: "データからレポートやダッシュボードを作成します。ビジネスデータをインサイトに変えます。" },
      bpm: { name: "プロセス", description: "内部申請、承認、タスクをデジタル環境で開始、追跡、完了します。" },
      support: { name: "サポート", description: "顧客のリクエストとチケットを管理します。問題を一箇所で追跡して解決します。" },
      agent: { name: "自動化", description: "ボットとエージェントを使用してルーチンタスクを自動化します。ツールと統合します。" },
      ai: { name: "AIと予測", description: "AIモデルを使用してデータ分析と予測を行います。計画と意思決定を改善します。" },
      erp: { name: "ビジネススイート", description: "財務、販売、在庫、オペレーション用モジュール。一つの平台でビジネスを運営します。" },
      pms: { name: "プロジェクト", description: "プロジェクトを計画し、タスクを割り当て、進捗を追跡し、チームで協力します。" },
      crm: { name: "顧客管理", description: "連絡先、リード、案件、販売パイプラインを管理します。すべての顧客情報を集約します。" },
    },
    menus: {
      core_dashboard: "ダッシュボード",
      core_companies: "企業",
      core_organizations: "組織",
      core_roles: "役割",
      core_users: "ユーザー",
      core_groups: "グループ",
      core_emails: "メールアカウント",
      core_data_sources: "データソース",
      core_settings_company: "企業設定",
      core_settings_user: "ユーザー設定",
      core_translations: "翻訳",
    },
  },

  panel: {
    apps: {
      crm: { name: "CRM", footerName: "CRM" },
      accounting: { name: "会計" },
      finance: { name: "財務" },
      procurement: { name: "調達" },
      inventory: { name: "在庫" },
      purchase: { name: "購買" },
      sales: { name: "販売" },
      manufacturing: { name: "製造" },
      assets: { name: "資産" },
      hr: { name: "人事" },
      project: { name: "プロジェクト管理" },
      timesheet: { name: "タイムシート" },
      documents: { name: "ドキュメント" },
      pos: { name: "POS" },
      rental: { name: "レンタル" },
      dashboard: { name: "レポート" },
    },
    appsLabel: "アプリケーション",
    settingsLabel: "設定",
    coreLabel: "管理",
    backToHome: "ホームに戻る",
  },

  errors: {
    unknown: "不明なエラーが発生しました",
    network: "サーバーに接続できません。インターネット接続を確認してください。",
    timeout: "タイムアウトしました。もう一度お試しください。",
    badRequest: "不正なリクエストです",
    unauthorized: "セッションが切れました。ログインし直してください。",
    forbidden: "この操作を実行する権限がありません。",
    notFound: "要求されたリソースが見つかりません",
    tooManyRequests: "リクエストが多すぎます。後でもう一度お試しください。",
    internalServerError: "サーバーエラーです。後でお試しください。",
    badGateway: "サーバーが一時的に利用不能です",
    serviceUnavailable: "サービスが一時的に利用不能です",
    gatewayTimeout: "サーバーから応答がありません",
    serverErrorWithStatus: "サーバーエラー ({status})",
  },

  // Dashboard
  dashboard: {
    title: "ダッシュボード",
    subtitle: "一元管理システムの概要",
    welcome: "ようこそ",
    guestName: "ゲスト",
    statusSummary: "現在の状況の概要",
    currentTimeLabel: "現在の時刻",
    todayLabel: "今日",
    activeCompanies: "有効な企業",
    activeOrganizations: "有効な組織",
    activeUsers: "有効なユーザー",
    activeRoles: "有効な役割",
    database: "データベース",
    databaseUsed: "使用容量",
    recentlyAddedUsers: "最近追加されたユーザー",
    recentlyAddedCompanies: "最近追加された企業",
    noUsersYet: "ユーザーはまだいません",
    noCompaniesYet: "企業はまだありません",
    userPanel: "ユーザーパネル",
    userPanelWelcome: "ようこそ！ユーザー設定はメニューからアクセスできます。",
    quickActions: "クイックアクション",
    systemHealthTitle: "システムの状態",
    systemLogsTitle: "システムログ",
    logChartTitle: "エラー・警告の分布",
    logErrorsLabel: "エラー",
    logWarningsLabel: "警告",
    logSuccessLabel: "成功",
    statusHealthy: "正常",
    statusFailed: "失敗",
    statusUnknown: "不明",
    lastRun: "最終実行",
    failures24h: "24時間以内の失敗",
    noData: "データなし",
    systemComponents: {
      EMAIL_INGEST: "受信メール (IMAP)",
      EMAIL_DISPATCH: "送信メール (SMTP)",
      JOB_SCHEDULER: "スケジューラ",
      API: "API",
    },
  },

  // Sidebar
  sidebar: {
    dashboard: "ダッシュボード",
    roles: "役割",
    users: "ユーザー",
    settings: "設定",
    systemSettings: "統合と設定",
    userSettings: "ユーザー設定",
    organizations: "組織",
    logout: "ログアウト",
    toggleMenu: "メニューを切り替え",
    toggleMenuClose: "メニューを閉じる",
    management: "管理",
    companies: "企業",
    emailAccounts: "メールアカウント",
    dataSources: "データソース",
    translations: "翻訳",
  },

  // Settings
  settings: {
    title: "設定",
    subtitle: "企業とユーザーの設定を管理",
    companySettingsSubtitle: "企業の一般設定",
    userSettingsSubtitle: "個人設定",
    securitySettingsSubtitle: "パスワードとセキュリティ設定",
    companySettings: "企業設定",
    userSettings: "ユーザー設定",
    general: "一般",
    personal: "個人",
    timezone: "タイムゾーン",
    dateFormat: "日付形式",
    defaultCurrency: "デフォルトの通貨",
    defaultLanguage: "デフォルトの言語",
    language: "言語",
    companySettingsSaved: "企業設定を保存しました",
    userSettingsSaved: "ユーザー設定を保存しました",
    failedToLoad: "設定の読み込みに失敗しました",
    saveFailed: "設定の保存に失敗しました",
    profilePicture: "プロフィール写真",
    maxFileSize: "最大サイズ: 5MB。形式: JPEG, PNG, GIF, WebP",
    change: "変更",
    uploading: "アップロード中...",
    deleteAvatar: "写真を削除",
    deleteAvatarConfirm: "プロフィール写真を削除しますか？",
    avatarUploaded: "写真をアップロードしました",
    avatarDeleted: "写真を削除しました",
    avatarUploadError: "アップロードエラーが発生しました",
    avatarDeleteError: "削除エラーが発生しました",
    onlyImageFiles: "画像ファイルのみ許可されています",
    fileSizeError: "ファイルサイズは5MB未満にしてください",
    appearance: "外観",
    theme: "テーマ",
    light: "ライト",
    dark: "ダーク",
    pageSize: "テーブルのページサイズ",
    defaultCompany: "デフォルトの企業",
    selectDefaultCompany: "デフォルトの企業を選択してください",
    noCompaniesAssigned: "割り当てられた企業がありません",
    security: "セキュリティ",
    changePassword: "パスワードを変更",
    currentPassword: "現在のパスワード",
    newPassword: "新しいパスワード",
    confirmNewPassword: "新しいパスワード（確認）",
    passwordChanged: "パスワードを変更しました",
    passwordChangeFailed: "パスワードの変更に失敗しました",
    passwordMismatch: "新しいパスワードが一致しません",
    passwordTooShort: "パスワードは6文字以上にしてください",
    passwordSameAsOld: "新しいパスワードは現在のパスワードと異なる必要があります",
    changingPassword: "パスワードを変更中...",
    pleaseSelectCompany: "企業を選択してください",
  },

  // Organizations
  organizations: {
    title: "組織",
    code: "コード",
    name: "組織名",
    description: "説明",
    logo: "ロゴ",
    logoEmpty: "ロゴなし",
    uploadLogo: "ロゴをアップロード",
    status: "ステータス",
    smtp: "SMTP",
    imap: "IMAP",
    smtpEmailAccount: "SMTPメールアカウント",
    imapEmailAccount: "IMAPメールアカウント",
    active: "有効",
    inactive: "無効",
    newOrganization: "新規組織",
    editOrganization: "組織を編集",
    deleteConfirm: "この組織を削除しますか？",
    deleteFailed: "組織の削除に失敗しました",
    operationFailed: "操作に失敗しました",
    failedToLoad: "組織の読み込みに失敗しました",
    dataFormatError: "組織データの形式が不正です",
    searchPlaceholder: "組織を検索（名前、説明）",
    empty: "データなし",
    notSet: "未設定",
    noCreatePermission: "組織を作成する権限がありません",
    noUpdatePermission: "組織を編集する権限がありません",
    noDeletePermission: "組織を削除する権限がありません",
    company: "企業名",
    statusDistribution: "組織のステータス分布",
  },

  // Users
  users: {
    title: "ユーザー",
    newUser: "新規ユーザー",
    editUser: "ユーザーを編集",
    noUsersYet: "ユーザーはまだいません",
    failedToLoad: "ユーザーの読み込みに失敗しました",
    deleteConfirm: "このユーザーを削除しますか？",
    deleteFailed: "ユーザーの削除に失敗しました",
    operationFailed: "操作に失敗しました",
    passwordRequired: "パスワードは必須です",
    passwordChangeHint: "（変更する場合のみ入力）",
    fullName: "フルネーム",
    email: "メールアドレス",
    status: "ステータス",
    roles: "役割",
    actions: "操作",
    firstName: "名 *",
    lastName: "姓 *",
    password: "パスワード",
    noRoles: "役割なし",
    rolesNotFound: "役割が見つかりません",
    statusActive: "有効",
    statusInactive: "無効",
    statusSuspended: "一時停止",
    userInfoFailed: "ユーザー情報の取得に失敗しました",
    rolesFailed: "役割の取得に失敗しました",
    copy: "コピー",
    isAdmin: "管理者",
    groups: "グループ",
    groupCount: "グループ数",
    userGroupName: "グループ名",
    userGroupCode: "グループコード",
    userGroupEmail: "グループメール",
    groupManager: "グループ管理者",
    members: "メンバー",
    userGroupDescription: "説明",
    userGroupCreate: "新規グループ作成",
    userGroupEdit: "グループを編集",
    manageMembers: "メンバーを管理",
    addMember: "メンバーを追加",
    memberCount: "メンバー数",
    searchUsers: "ユーザーを検索...",
    noMembers: "メンバーはまだいません",
    selectUser: "ユーザーを選択",
    addUser: "追加",
    removeUser: "削除",
    manageGroups: "グループを管理",
    noUsersFound: "ユーザーが見つかりません",
    statusDistribution: "ユーザーのステータス分布",
    groupStatusDistribution: "グループのステータス分布",
  },

  // Roles
  roles: {
    title: "役割",
    newRole: "新規役割",
    editRole: "役割を編集",
    noRolesYet: "役割はまだありません",
    failedToLoad: "役割の読み込みに失敗しました",
    deleteConfirm: "この役割を削除しますか？",
    deleteFailed: "役割の削除に失敗しました",
    operationFailed: "操作に失敗しました",
    dataFormatError: "データ形式エラー",
    permissionsFailed: "権限の取得に失敗しました",
    name: "役割名 *",
    description: "説明",
    permissions: "権限",
    permissionCount: "権限数",
    type: "役割のタイプ",
    typeSystem: "システム",
    typeCustom: "カスタム",
    typeAll: "すべての役割",
    searchPlaceholder: "検索（役割名、説明、権限）...",
    systemBadge: "システム",
    permissionsCount: "権限 ({count})",
    morePermissions: "他 {count} 件",
    copy: "コピー",
    copySuffix: "（コピー）",
    clearFilters: "フィルターをクリア",
    noCreatePermission: "役割を作成する権限がありません",
    noUpdatePermission: "役割を編集する権限がありません",
    noDeletePermission: "役割を削除する権限がありません",
    systemRoleEditBlocked: "システム役割は編集できません",
    systemRoleDeleteBlocked: "システム役割は削除できません",
    noPermissions: "権限なし",
    paginationRange: "{total} 件中 {start}-{end}",
    paginationPage: "{total} ページ中 {page} ページ目",
    modules: {
      users: "ユーザー",
      groups: "グループ",
      userGroupName: "グループ名",
      userGroupCode: "グループコード",
      groupManager: "グループ管理者",
      members: "メンバー",
      userGroupDescription: "説明",
      userGroupCreate: "新規グループ作成",
      userGroupEdit: "グループを編集",
      roles: "役割",
      tenant: "テナント",
      userSettingField: "設定",
      settings: "設定",
      organizations: "組織",
    },
    groups: "グループ",
    userGroup: "ユーザーグループ",
    userGroupList: "グループ一覧",
    userGroupCreate: "グループ作成",
    userGroupEdit: "グループを編集",
    userGroupName: "グループ名",
    userGroupDescription: "説明",
    userGroupCode: "コード",
    groupManager: "グループ管理者",
    members: "メンバー",
    addMember: "メンバーを追加",
    assignRole: "役割を割り当て",
    assign: "割り当て",
    statusDistribution: "役割のステータス分布",
    assignRoleMatrix: "役割マトリックス",
    roleMatrix: "役割マトリックス",
    selectCompanyToViewMatrix: "マトリックスを表示する企業を選択してください",
    show: "表示",
    hide: "非表示",
    searchPermissions: "権限を検索...",
    permission: "権限",
    permissionsSelected: "{count} 個の権限を選択中",
  },

  // Companies
  companies: {
    title: "企業",
    newCompany: "新規企業",
    editCompany: "企業を編集",
    noCompaniesYet: "企業はまだありません",
    failedToLoad: "企業の読み込みに失敗しました",
    deleteConfirm: "この企業を削除しますか？",
    deleteFailed: "企業の削除に失敗しました",
    operationFailed: "操作に失敗しました",
    dataFormatError: "データ形式エラー",
    name: "企業名 *",
    status: "ステータス",
    statusActive: "有効",
    statusInactive: "無効",
    searchPlaceholder: "検索（企業名、ステータス）...",
    clearFilters: "フィルターをクリア",
    copy: "コピー",
    copySuffix: "（コピー）",
    noCreatePermission: "企業を作成する権限がありません",
    noUpdatePermission: "企業を編集する権限がありません",
    noDeletePermission: "企業を削除する権限がありません",
    paginationRange: "{total} 件中 {start}-{end}",
    paginationPage: "{total} ページ中 {page} ページ目",
    allStatuses: "すべてのステータス",
    statusDistribution: "企業のステータス分布",
    show: "表示",
    hide: "非表示",
    logo: "ロゴ",
    logoEmpty: "ロゴなし",
    uploadLogo: "ロゴをアップロード",
    smtpEmailAccount: "SMTPメールアカウント",
    imapEmailAccount: "IMAPメールアカウント",
    smtpEmailId: "SMTPメールアカウント",
    imapEmailId: "IMAPメールアカウント",
  },

  // Emails
  emails: {
    title: "メールアカウント",
    newEmail: "新規アカウント",
    editEmail: "アカウントを編集",
    noEmailsYet: "アカウントはまだありません",
    failedToLoad: "読み込みに失敗しました",
    deleteConfirm: "このアカウントを削除しますか？",
    deleteConfirmMessage: "「{name}」を削除してもよろしいですか？",
    deleteFailed: "削除に失敗しました",
    operationFailed: "操作に失敗しました",
    dataFormatError: "データ形式エラー",
    name: "アカウント名",
    type: "タイプ",
    host: "ホスト",
    port: "ポート",
    user: "ユーザー名",
    password: "パスワード",
    fromEmail: "送信元メールアドレス",
    secure: "セキュリティ (SSL/TLS)",
    secureConnection: "安全な接続 (SSL/TLS)",
    status: "ステータス",
    statusActive: "有効",
    statusInactive: "無効",
    activate: "有効化",
    deactivate: "無効化",
    isActive: "有効",
    searchPlaceholder: "検索（名前、ホスト、ユーザー）...",
    clearFilters: "フィルターをクリア",
    copy: "コピー",
    noCreatePermission: "作成権限がありません",
    noUpdatePermission: "編集権限がありません",
    noDeletePermission: "削除権限がありません",
    paginationRange: "{total} 件中 {start}-{end}",
    paginationPage: "{total} ページ中 {page} ページ目",
    allStatuses: "すべてのステータス",
    allTypes: "すべてのタイプ",
    statusDistribution: "アカウントのステータス分布",
    show: "表示",
    hide: "非表示",
    nameRequired: "アカウント名は必須です",
    hostRequired: "ホストは必須です",
    portRequired: "ポートは必須です",
    userRequired: "ユーザー名は必須です",
    passwordRequired: "パスワードは必須です",
    leaveBlankToKeep: "変更しない場合は空のままにします",
    noData: "データなし",
    noEmailsMessage: "アカウントが見つかりません",
    passwordEditHint: "変更しない場合は空のままにします",
    noSearchResults: "検索結果が見つかりません",
    namePlaceholder: "例: サポート または info@company.com",
    deleteConfirmTitle: "アカウント削除",
  },

  // Login
  login: {
    title: "QubePro ERP",
    subtitle: "エンタープライズリソースプランニング",
    heroTitle: "企業管理を一つの場所で",
    heroSubtitle: "QubePro ERPは、企業、組織、ユーザー、役割の管理を一つの安全なプラットフォームに統合します。",
    languageToggleLabel: "言語",
    turkish: "トルコ語",
    english: "英語",
    email: "メールアドレス",
    password: "パスワード",
    emailPlaceholder: "example@domain.com",
    emailHint: "テナントはドメインから自動的に判別されます",
    passwordPlaceholder: "••••••••",
    loginButton: "ログイン",
    loggingIn: "ログイン中...",
    loginFailed: "ログインに失敗しました",
    darkMode: "ダークモード",
    lightMode: "ライトモード",
    loginPageTitle: "ログイン",
    submitButton: "ログイン",
    featureTitle: "一元管理システム",
    featureDescription: "顧客関係、販売、在庫、財務プロセスを1つの統合プラットフォームで管理します。",
    featureCard1Title: "顧客管理",
    featureCard1Description: "顧客、見込み販売、ビジネス関係を一元管理します。",
    featureCard2Title: "販売と注文",
    featureCard2Description: "見積もり、注文、販売プロセスを簡単に管理し、収益を伸ばします。",
    featureCard3Title: "レポートと分析",
    featureCard3Description: "リアルタイムのレポートとダッシュボードでビジネスパフォーマンスを監視し、データドリブンな意思決定を行います。",
    featureCard4Title: "システム設定",
    featureCard4Description: "システムをカスタマイズし、他のツールと統合して、ビジネスプロセスを自動化します。",
    pleaseWait: "少々お待ちください",
    serverConfiguring: "サーバー構成中、少々お待ちください...",
    dbCreating: "データベースと必要な設定を作成しています。数秒かかる場合があります。",
    emailLabel: "メールアドレス",
    passwordLabel: "パスワード",
    emailPlaceholderFull: "メールアドレスを入力してください",
    passwordPlaceholderFull: "パスワードを入力してください",
    showPassword: "表示",
    hidePassword: "隠す",
    emailRequired: "メールアドレスは必須です",
    passwordRequired: "パスワードは必須です",
    backendUnavailable: "サーバーを利用できません。後でやり直してください。",
    backendConnectFailed: "サーバーに接続できませんでした。後でやり直してください。",
    backendConnectFailedDev: "サーバーに接続できませんでした。サーバーが起動しているか確認してください ({apiUrl})。",
    fieldEmail: "メール",
    fieldPassword: "パスワード",
    errors: {
      invalidCredentials: "メールアドレスまたはパスワードが正しくありません。",
      databaseUnavailable: "データベースを利用できないか、設定が必要です。ログインし直してください。",
      accountInactive: "アカウントが無効です。管理者に連絡してください。",
      sessionExpired: "セッションの期限が切れました。ログインし直してください。",
      tooManyAttempts: "試行回数が多すぎます。後でやり直してください。",
      sessionTooLarge: "セッションデータが大きすぎます。ログインし直してください。",
    },
  },

  // DataSources
  dataSources: {
    title: "データソース",
    subtitle: "顧客データベースへの接続を管理",
    newDataSource: "新規データソース",
    editDataSource: "データソースを編集",
    noDataSourcesYet: "データソースはまだありません",
    failedToLoad: "データソースの読み込みに失敗しました",
    deleteConfirm: "このデータソースを削除しますか？",
    deleteFailed: "削除に失敗しました",
    operationFailed: "操作に失敗しました",
    dataFormatError: "データ形式エラー",
    name: "表示名",
    driver: "ドライバー",
    server: "サーバー",
    port: "ポート",
    databaseName: "データベース名",
    username: "ユーザー名",
    password: "パスワード",
    schema: "スキーマ",
    sourceType: "ソースタイプ",
    source: "ソース",
    target: "ターゲット",
    sourceSystem: "ソースシステム",
    targetSystem: "ターゲットシステム",
    status: "ステータス",
    statusActive: "有効",
    statusInactive: "無効",
    searchPlaceholder: "検索（名前、サーバー、データベース）...",
    clearFilters: "フィルターをクリア",
    copy: "コピー",
    paginationRange: "{total} 件中 {start}-{end}",
    paginationPage: "{total} ページ中 {page} ページ目",
    allStatuses: "すべてのステータス",
    allTypes: "すべて",
    statusDistribution: "データソースのステータス分布",
    show: "表示",
    hide: "非表示",
    nameRequired: "名前は必須です",
    serverRequired: "サーバーアドレスは必須です",
    databaseRequired: "データベース名は必須です",
    passwordRequired: "パスワードは必須です",
    testConnection: "接続テスト",
    testing: "テスト中...",
    testSuccess: "接続に成功しました！",
    testFailed: "接続に失敗しました: ",
    integratedSecurity: "統合セキュリティ",
    ssl: "SSL",
    connectionTimeout: "タイムアウト（秒）",
    description: "説明",
    noCreatePermission: "データソースを作成する権限がありません",
    noUpdatePermission: "データソースを編集する権限がありません",
    noDeletePermission: "データソースを削除する権限がありません",
  },

  aiProviders: {
    title: "AIプロバイダー",
    subtitle: "AIモデルと統合の管理",
    newProvider: "新規AIプロバイダー",
    editProvider: "AIプロバイダーの編集",
    noProvidersYet: "データなし",
    failedToLoad: "AIプロバイダーの読み込みに失敗しました",
    deleteConfirm: "このAIプロバイダーを削除してもよろしいですか？",
    deleteFailed: "AIプロバイダーの削除に失敗しました",
    operationFailed: "操作に失敗しました",
    name: "プロバイダー名 *",
    provider: "プロバイダーの種類",
    baseUrl: "ベースURL",
    apiKey: "APIキー",
    description: "説明",
    status: "ステータス",
    statusActive: "有効",
    statusInactive: "無効",
    searchPlaceholder: "検索 (名前、タイプ、URL)...",
    clearFilters: "フィルターをクリア",
    noCreatePermission: "AIプロバイダーを作成する権限がありません",
    statusDistribution: "AIプロバイダーのステータス分布",
    baseUrlPlaceholder: "例: http://localhost:11434, https://api.custom.ai/v1",
    baseUrlEmptyDefault: "デフォルトの場合は空のままにします",
    apiKeyPlaceholder: "APIキーを入力",
  },
};
